(() => {
  const VERSION = '2026-08-12.1';
  if (window.PBCustomizerRepairV1?.version === VERSION) return;

  const V = window.PBVariantV2;
  const ROOT_SELECTOR = '[id^="PBPhotoBoxCustomizer-"], [id^="PBUSBBoxCustomizer-"], [id^="PBStandaloneUSBCustomizer-"]';
  const MODAL_SELECTOR = '.pb-photo-customizer__modal, .pb-usb-customizer__modal, .pb-standalone-usb-customizer__modal, [data-pb-customizer-modal]';
  const contexts = [];
  let paidRequestIds = new Set();
  let cartLoaded = false;
  let cartLoading = null;

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];
  const normalize = value => V?.normalize ? V.normalize(value) : String(value || '').trim().toLowerCase();
  const parse = (root, selector, fallback) => {
    try { return JSON.parse(q(root, selector)?.textContent || JSON.stringify(fallback)); }
    catch { return fallback; }
  };
  const active = (root, selector) => q(root, `${selector}.is-active`);
  const activeData = (root, selector, key, fallback = '') => active(root, selector)?.dataset?.[key] || fallback;

  const injectStyles = () => {
    if (document.getElementById('pb-customizer-repair-v1-style')) return;
    const style = document.createElement('style');
    style.id = 'pb-customizer-repair-v1-style';
    style.textContent = `
      .pb-repair-message-proxy{display:none!important}
      .pb-repair-toast{position:fixed;left:50%;bottom:2.2rem;transform:translateX(-50%);z-index:2147483000;width:min(92vw,62rem);padding:1.15rem 1.35rem;border:1px solid rgba(120,35,25,.28);border-radius:8px;background:#fff8f6;color:#5f211a;box-shadow:0 8px 28px rgba(0,0,0,.16);font-size:1.35rem;line-height:1.4}
      .pb-repair-toast[hidden]{display:none!important}
      .pb-repair-config-note{margin:.75rem 0 0;padding:.85rem 1rem;border:1px solid rgba(var(--color-foreground),.16);border-radius:6px;background:rgba(var(--color-foreground),.035);font-size:1.2rem;line-height:1.4}
      .pb-repair-config-note[hidden]{display:none!important}
    `;
    document.head.appendChild(style);
  };

  const ensureToast = ctx => {
    if (ctx.toast) return ctx.toast;
    const toast = document.createElement('div');
    toast.className = 'pb-repair-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.hidden = true;
    document.body.appendChild(toast);
    ctx.toast = toast;
    return toast;
  };

  const showToast = (ctx, text) => {
    const toast = ensureToast(ctx);
    toast.textContent = text || '';
    toast.hidden = !text;
    if (text) {
      clearTimeout(ctx.toastTimer);
      ctx.toastTimer = setTimeout(() => { toast.hidden = true; }, 9000);
    }
  };

  const ensureMessageProxy = ctx => {
    let proxy = q(ctx.root, ':scope > [data-pb-message].pb-repair-message-proxy');
    if (proxy) return proxy;

    // Logo V3 still calls message(root, ...), but the live modal is moved out of root.
    // Keep a dedicated message target directly under root. Before the modal moves, the
    // native in-modal message can still win querySelector; after it moves, this proxy
    // becomes the stable target and mirrors the message into a visible toast.
    proxy = document.createElement('div');
    proxy.className = 'pb-repair-message-proxy';
    proxy.dataset.pbMessage = '';
    proxy.hidden = true;
    ctx.root.appendChild(proxy);

    const mirror = () => {
      const text = String(proxy.textContent || '').trim();
      if (!proxy.hidden && text) showToast(ctx, text);
      else if (!text && ctx.toast) ctx.toast.hidden = true;
    };
    new MutationObserver(mirror).observe(proxy, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
    ctx.messageProxy = proxy;
    return proxy;
  };

  const readCartPaidRequests = async (fresh = false) => {
    if (!fresh && cartLoaded) return paidRequestIds;
    if (!fresh && cartLoading) return cartLoading;
    cartLoading = (async () => {
      try {
        const cart = window.PBLogoV3?.getCart
          ? await window.PBLogoV3.getCart()
          : await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then(r => r.json());
        const paid = new Set();
        (cart?.items || []).forEach(item => {
          const props = item.properties || {};
          if (props._PB_Internal_Fee !== 'Logo Die Setup') return;
          if (props._PB_Die_Request_ID) paid.add(String(props._PB_Die_Request_ID));
        });
        paidRequestIds = paid;
        cartLoaded = true;
      } catch (error) {
        console.warn('PB repair could not inspect cart setup lines', error);
      } finally {
        cartLoading = null;
      }
      return paidRequestIds;
    })();
    return cartLoading;
  };

  const normalizeDieCandidate = candidate => {
    if (!candidate || typeof candidate !== 'object') return candidate;

    if (candidate.source === 'saved-new-profile') {
      candidate.source = 'saved';
      candidate.dieId = candidate.dieId || candidate.sourceDieId;
      candidate.requiresSetup = false;
      candidate.needsUpload = false;
      candidate.detail = 'Saved logo — ready to reuse';
    }

    if (candidate.source === 'cart-new-profile') {
      candidate.source = 'cart';
      candidate.requestId = candidate.requestId || candidate.sourceRequestId;
    }

    if (candidate.source === 'saved') {
      candidate.requiresSetup = false;
      candidate.needsUpload = false;
      candidate.detail = 'Saved logo — ready to reuse';
    }

    if (candidate.source === 'cart') {
      const requestId = String(candidate.requestId || candidate.sourceRequestId || '');
      candidate.requestId = requestId;
      candidate.requiresSetup = requestId ? !paidRequestIds.has(requestId) : true;
      candidate.detail = candidate.requiresSetup
        ? 'Already uploaded for this order — setup applies once'
        : 'Already uploaded for this order — ready to reuse';
    }
    return candidate;
  };

  const syncDieCandidates = async ctx => {
    await readCartPaidRequests();
    qa(ctx.scope, '.pb-logo-v3__source').forEach(button => {
      const candidate = normalizeDieCandidate(button._pbCandidate);
      if (!candidate) return;
      const copy = [...button.children].find(el =>
        el.tagName === 'SPAN' &&
        !el.classList.contains('pb-logo-phase1__thumb') &&
        !el.classList.contains('pb-logo-v3__source-price')
      );
      const detail = copy?.querySelector?.('small');
      const price = q(button, '.pb-logo-v3__source-price');
      if (detail && candidate.detail) detail.textContent = candidate.detail;
      if (price) {
        if (candidate.source === 'saved') price.textContent = 'No setup fee';
        if (candidate.source === 'cart') price.textContent = candidate.requiresSetup ? '+$30 setup' : 'No setup fee';
      }
    });
  };

  const familyFor = root => {
    if (root.id.startsWith('PBStandaloneUSBCustomizer-')) return 'standalone';
    if (root.id.startsWith('PBUSBBoxCustomizer-')) return 'usb-box';
    return 'photo';
  };

  const setOwner = (ctx, owner) => {
    if (!owner) return;
    ctx.owner = owner;
    ctx.scope.dataset.pbPreviewOwner = owner;
    scheduleSync(ctx);
  };

  const boxTextActive = ctx => !!active(ctx.scope, '[data-personalization="personalized"]');
  const boxLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="box"]');
  const usbTextActive = ctx => !!active(ctx.scope, '[data-usb-engraving="yes"]');
  const usbLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="usb"]');
  const addonTextActive = ctx => !!active(ctx.scope, '[data-box-personalization="personalized"]');
  const addonLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="addon"]');

  const syncPreviewOwner = ctx => {
    const owner = ctx.owner || ctx.scope.dataset.pbPreviewOwner || (ctx.family === 'standalone' ? 'usb' : 'box');
    ctx.owner = owner;
    ctx.scope.dataset.pbPreviewOwner = owner;

    qa(ctx.scope, '[data-pb-logo-v3-preview]').forEach(preview => {
      const kind = preview.dataset.pbLogoV3Preview || '';
      const shouldShow = (owner === 'box' && kind === 'box' && boxLogoActive(ctx)) ||
        (owner === 'usb' && kind === 'usb' && usbLogoActive(ctx)) ||
        (owner === 'addon' && kind === 'addon' && addonLogoActive(ctx));
      preview.hidden = !shouldShow;
    });

    const text = q(ctx.scope, '[data-pb-preview-text]');
    if (text) {
      const shouldShowText = (owner === 'box' && boxTextActive(ctx) && !boxLogoActive(ctx)) ||
        (owner === 'usb' && usbTextActive(ctx) && !usbLogoActive(ctx)) ||
        (owner === 'addon' && addonTextActive(ctx) && !addonLogoActive(ctx));
      if (shouldShowText) {
        text.style.removeProperty('display');
        text.removeAttribute('aria-hidden');
      } else {
        text.style.setProperty('display', 'none', 'important');
        text.setAttribute('aria-hidden', 'true');
      }
    }
  };

  const boxVariantStatus = (ctx, kind) => {
    if (!V) return null;
    if (kind === 'box') {
      const variants = parse(ctx.root, '[data-pb-variants-json]', []);
      const color = activeData(ctx.scope, '[data-color]', 'color', 'Cream');
      const variant = V.resolveVariant(variants, { domain: 'box', state: 'logo', requiredValues: [color] });
      return { variant, label: `foil logo for ${color}` };
    }
    if (kind === 'addon') {
      const products = parse(ctx.root, '[data-pb-box-products-json]', {});
      const key = activeData(ctx.scope, '[data-box-key]', 'boxKey');
      const color = activeData(ctx.scope, '[data-box-color]', 'boxColor');
      const variants = products?.[key]?.variants || [];
      const variant = V.resolveVariant(variants, { domain: 'box', state: 'logo', requiredValues: [color] });
      return { variant, label: 'foil logo for this presentation box' };
    }
    if (kind === 'usb') {
      const capacity = activeData(ctx.scope, '[data-usb-capacity]', 'usbCapacity', '8GB');
      let variants = [];
      if (ctx.family === 'standalone') variants = parse(ctx.root, '[data-pb-variants-json]', []);
      else {
        const products = parse(ctx.root, '[data-pb-usb-products-json]', {});
        const key = activeData(ctx.scope, '[data-usb-key]', 'usbKey');
        variants = products?.[key]?.variants || [];
      }
      const variant = V.resolveVariant(variants, { domain: 'usb', state: 'logo', requiredValues: [capacity] });
      return { variant, label: `logo engraving for ${capacity}` };
    }
    return null;
  };

  const configMessage = status => {
    if (!status) return '';
    if (!status.variant) return `This ${status.label} is not configured in Shopify yet.`;
    if (!status.variant.available) return `This ${status.label} exists in Shopify but is currently unavailable for sale.`;
    return '';
  };

  const ensureConfigNote = ctx => {
    let note = q(ctx.scope, '[data-pb-repair-config-note]');
    if (note) return note;
    note = document.createElement('div');
    note.className = 'pb-repair-config-note';
    note.dataset.pbRepairConfigNote = '';
    note.hidden = true;
    const add = q(ctx.scope, '[data-pb-add]');
    if (add?.parentElement) add.parentElement.insertBefore(note, add);
    else ctx.scope.appendChild(note);
    return note;
  };

  const syncConfigurationState = ctx => {
    const selectedKind = boxLogoActive(ctx) ? 'box' : usbLogoActive(ctx) ? 'usb' : addonLogoActive(ctx) ? 'addon' : '';
    const note = ensureConfigNote(ctx);
    if (!selectedKind) {
      note.hidden = true;
      note.textContent = '';
      return;
    }
    const msg = configMessage(boxVariantStatus(ctx, selectedKind));
    note.textContent = msg;
    note.hidden = !msg;

    const logoButton = q(ctx.scope, `[data-pb-logo="${selectedKind}"]`);
    const price = q(logoButton, '[data-pb-v22-price]');
    if (price && msg) price.textContent = msg.includes('not configured') ? 'Not configured' : 'Unavailable';
  };

  const sync = ctx => {
    syncPreviewOwner(ctx);
    syncConfigurationState(ctx);
    syncDieCandidates(ctx);
  };

  const scheduleSync = ctx => {
    if (!ctx || ctx.syncScheduled) return;
    ctx.syncScheduled = true;
    requestAnimationFrame(() => {
      ctx.syncScheduled = false;
      sync(ctx);
    });
    // Logo V3's older global text-mode patch also schedules delayed syncs.
    // Finish after those timers so explicit preview ownership remains authoritative.
    setTimeout(() => sync(ctx), 60);
    setTimeout(() => sync(ctx), 140);
    setTimeout(() => sync(ctx), 320);
  };

  const ownerFromTarget = (ctx, target) => {
    if (!(target instanceof Element)) return '';
    if (target.closest('[data-box-choice], [data-box-key], [data-box-color], [data-pb-box-personalization], [data-box-personalization], [data-box-foil-name], [data-pb-box-main-text], [data-pb-box-second-text], [data-pb-logo="addon"], [data-pb-logo-v3="addon"]')) return 'addon';
    if (target.closest('[data-usb-choice], [data-usb-key], [data-usb-capacity], [data-pb-usb-engraving], [data-usb-engraving], [data-pb-usb-engraving-text], [data-pb-usb-engraving-font], [data-pb-logo="usb"], [data-pb-logo-v3="usb"]')) return 'usb';
    if (target.closest('[data-color], [data-pb-personalization], [data-personalization], [data-foil], [data-pb-main-input], [data-pb-second-input], [data-pb-logo="box"], [data-pb-logo-v3="box"]')) return 'box';
    return '';
  };

  injectStyles();

  qa(document, ROOT_SELECTOR).forEach(root => {
    const scope = q(root, MODAL_SELECTOR) || root;
    const ctx = { root, scope, family: familyFor(root), owner: scope.dataset.pbPreviewOwner || '' };
    contexts.push(ctx);
    ensureMessageProxy(ctx);
    ensureConfigNote(ctx);
    sync(ctx);

    new MutationObserver(() => scheduleSync(ctx)).observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'src']
    });
  });

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const ctx = contexts.find(item => item.scope.contains(target) || item.root.contains(target));
    if (!ctx) return;

    const owner = ownerFromTarget(ctx, target);
    if (owner) setOwner(ctx, owner);

    const logoButton = target.closest('[data-pb-logo]');
    if (logoButton) {
      const kind = logoButton.dataset.pbLogo;
      setTimeout(() => {
        const msg = configMessage(boxVariantStatus(ctx, kind));
        if (msg) showToast(ctx, msg);
      }, 0);
    }

    setTimeout(() => scheduleSync(ctx), 0);
    setTimeout(() => scheduleSync(ctx), 100);
  }, true);

  document.addEventListener('input', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const ctx = contexts.find(item => item.scope.contains(target));
    if (!ctx) return;
    const owner = ownerFromTarget(ctx, target);
    if (owner) setOwner(ctx, owner);
  }, true);

  document.addEventListener('change', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const ctx = contexts.find(item => item.scope.contains(target) || item.root.contains(target));
    if (!ctx) return;
    const owner = ownerFromTarget(ctx, target);
    if (owner) setOwner(ctx, owner);
    setTimeout(() => scheduleSync(ctx), 0);
    setTimeout(() => scheduleSync(ctx), 80);
  }, true);

  readCartPaidRequests().then(() => contexts.forEach(sync));

  window.PBCustomizerRepairV1 = {
    preview: (root, owner) => {
      const ctx = contexts.find(item => item.root === root);
      if (!ctx) return;
      ctx.owner = owner;
      ctx.scope.dataset.pbPreviewOwner = owner;
      sync(ctx);
    },
    version: VERSION,
    refresh: () => contexts.forEach(sync),
    refreshCart: async () => {
      cartLoaded = false;
      await readCartPaidRequests(true);
      contexts.forEach(sync);
    }
  };
})();
